import { useEffect, useRef, useState, useCallback } from "react";
import parseAPNG, { APNG } from "apng-js";
import PlayerAssets from "../../PlayerAssets";

type PngPlayerProps = {
    rate?: number,
    autoPlay?: boolean,
    loop?: boolean,
    paused?: boolean,
    url: string,
    onComplete: (hash: string) => void,
    onStart: (hash?: string) => void,
    hash: string,
    style: React.CSSProperties,
    fallbackSrc: string,
    duration?: number
};

type PngPlayerFallbackStaticProps = {
    hash: string,
    url: string,
    duration?: number,
    style: React.CSSProperties,
    onComplete: (hash: string) => void,
    onStart: (hash: string) => void
};

function PngPlayerStaticFallback(props: PngPlayerFallbackStaticProps) {
    const { hash, url, duration, style, onComplete, onStart } = props;
    onStart(hash);
    setTimeout(() => { onComplete(hash); }, duration);
    console.log('PngPlayerStaticFallback');
    return <img style={style} role="presentation" src={url} alt="" />
}

function PngPlayer(props:PngPlayerProps) {

    const canvasRef = useRef<HTMLCanvasElement>(null);
    const apngRef = useRef<Error | APNG | null>(null);
    const playerRef = useRef<APNG["getPlayer"] | any | null>(null);

    const {autoPlay, rate, url, loop, onComplete, onStart, hash, paused, style, fallbackSrc, duration} = props;

    // {width:`${width*scale}px`,height:`${height*scale}px`}

    const [loaded, setLoaded] = useState<string|null>(null);
    const [playing, setPlaying] = useState<string|null>(null);
    const [error, setError] = useState<Error|null>(null);

    const play = useCallback(async () => {
        if(!playerRef.current) return;
        // console.log(`PngPlayer :: play()`);
        if(!playing) {
            setPlaying(hash);
            playerRef.current.play();
        }
    },[playing,hash]);

    const pause = useCallback(async () => {
        if(!playerRef.current) return;
        // console.log(`PngPlayer :: pause()`);
        if(playing) {
            setPlaying(null);
            playerRef.current.pause();
        }
    }, [playing]);

    const initPlayer = useCallback(async () => {

        const canvas = canvasRef.current!;

        const asset = await PlayerAssets.get(url);
        const buffer = await asset.arrayBuffer();
        apngRef.current = parseAPNG(buffer);
        if (apngRef.current instanceof Error) {
            setError(apngRef.current);
            return;
        }

        if (typeof apngRef.current.width !== 'number') {
            setError(new Error('apngRef.current INVALID WIDTH'));
            return;
        }

        // console.log(url, `width=${apngRef.current.width},height=${apngRef.current.height},playtime=${apngRef.current.playTime}`);

        apngRef.current.numPlays = loop ? 0 : 1;

        canvas.width = apngRef.current.width;
        canvas.height = apngRef.current.height;

        const ctx:CanvasRenderingContext2D = canvas.getContext('2d')!;
        
        const player = await apngRef.current.getPlayer(ctx);

        playerRef.current = player;
        playerRef.current.playbackRate = rate;
        playerRef.current.once('end', () => {
            onComplete(hash);
        });
        if(autoPlay) {
            onStart(hash);
            play();
        }
    }, [autoPlay, hash, loop, onComplete, onStart, play, rate, url]);

    // initialize player on mount
    useEffect(() => {
        if(loaded !== hash) {
            setLoaded(hash);
            initPlayer();
        }
    }, [hash, initPlayer, loaded, url]);
    
    useEffect(() => {
        if(!paused && !playing) {
            play();
        }
    }, [paused, play, playing]);
    
    useEffect(() => {
        if(paused && playing) {
            pause();
        }
    }, [playing, pause, paused]);

    return error ? (   
        <PngPlayerStaticFallback
            {...props}
            url={fallbackSrc}
            onStart={onStart}
            onComplete={onComplete}
            hash={hash}
            duration={duration}
            style={style}
        />     
    ) : (  
        <canvas style={style} ref={canvasRef} />
    );
};

PngPlayer.defaultProps = {
    rate: 1,
    autoPlay: true,
    paused: false,
    loop: false,
    duration: 5000,
    onComplete: (hash: string) => {},
    onStart: () => {}
};

export default PngPlayer;